// API service for video translation backend
const API_BASE_URL = 'https://orca-app-hqarx.ondigitalocean.app/http://51.20.109.105:8000';
// const API_BASE_URL = 'http://localhost:8000';
export interface ProcessVideoRequest {
  video: File;
  email: string;
  duration: number;
  cost: number;
  source_language: string;
  target_language: string;
  speed?: number; // 0.25-4.0, default 0.85
  add_subtitles?: boolean; // Add subtitles to video
}

export interface InitUploadRequest {
  email: string;
  duration: number;
  cost: number;
  source_language: string;
  target_language: string;
  filename: string;
  file_size: number;
  speed?: number;
  add_subtitles?: boolean;
}

export interface InitUploadResponse {
  task_id: string;
  upload_url: string;
  status_url: string;
}

export interface ProcessVideoResponse {
  task_id: string;
  status: 'uploading' | 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
  check_status_url?: string;
  detail?: string;
}

export interface ProgressMessage {
  step: 'upload' | 'extract' | 'transcribe' | 'translate' | 'tts' | 'mix' | 'mux' | 'complete' | 'error';
  progress: number;
  message: string;
  timestamp: string;
}

export interface TaskStatus {
  task_id: string;
  status: 'uploading' | 'pending' | 'processing' | 'completed' | 'failed';
  progress: string;
  upload_progress?: number; // Процент загрузки файла (0-100)
  created_at: string;
  completed_at: string | null;
  result_url: string | null;
  error: string | null;
  output_path?: string;
  cloned_voice_id?: string;
  email: string;
  duration: number;
  cost: number;
  source_language: string;
  target_language: string;
  speed: number;
  video_filename: string;
}

export interface PaymentNotificationRequest {
  email: string;
  download_url: string;
  cost: number;
  task_id: string;
}

export interface ReplaceAudioNotificationRequest {
  filename: string;
}

class ApiService {
  /**
   * Initialize upload (Step 1) - creates task without uploading file
   */
  async initUpload(request: InitUploadRequest): Promise<InitUploadResponse> {
    const formData = new FormData();
    formData.append('email', request.email);
    formData.append('duration', request.duration.toString());
    formData.append('cost', request.cost.toString());
    formData.append('source_language', request.source_language);
    formData.append('target_language', request.target_language);
    formData.append('filename', request.filename);
    formData.append('file_size', request.file_size.toString());
    
    // Add speed parameter if provided (default is 0.85)
    if (request.speed !== undefined) {
      formData.append('speed', request.speed.toString());
    } else {
      formData.append('speed', '0.85');
    }

    // Add subtitles parameter if provided
    if (request.add_subtitles !== undefined) {
      formData.append('add_subtitles', request.add_subtitles.toString());
    }

    const response = await fetch(`${API_BASE_URL}/upload/init`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to initialize upload');
    }

    return response.json();
  }

  /**
   * Upload video file (Step 2) - with progress tracking
   */
  async uploadVideo(
    taskId: string, 
    video: File, 
    onProgress?: (percent: number) => void
  ): Promise<ProcessVideoResponse> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('video', video);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Failed to parse response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.detail || 'Failed to upload video'));
          } catch {
            reject(new Error('Failed to upload video'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('POST', `${API_BASE_URL}/upload/${taskId}`);
      xhr.send(formData);
    });
  }

  /**
   * Process video file (Legacy method - for backward compatibility)
   */
  async processVideo(request: ProcessVideoRequest): Promise<ProcessVideoResponse> {
    const formData = new FormData();
    formData.append('video', request.video);
    formData.append('email', request.email);
    formData.append('duration', request.duration.toString());
    formData.append('cost', request.cost.toString());
    formData.append('source_language', request.source_language);
    formData.append('target_language', request.target_language);
    
    // Add speed parameter if provided (default is 0.85)
    if (request.speed !== undefined) {
      formData.append('speed', request.speed.toString());
    } else {
      formData.append('speed', '0.85');
    }

    // Add subtitles parameter if provided
    if (request.add_subtitles !== undefined) {
      formData.append('add_subtitles', request.add_subtitles.toString());
    }

    const response = await fetch(`${API_BASE_URL}/process`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to process video');
    }

    return response.json();
  }

  /**
   * Notify backend when user clicks "Replace Audio"
   */
  async notifyReplaceAudio(request: ReplaceAudioNotificationRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notify/replace-audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: request.filename,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to notify replace-audio click');
    }
  }

  /**
   * Check task status
   */
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    const response = await fetch(`${API_BASE_URL}/status/${taskId}`);
    
    if (!response.ok) {
      throw new Error('Failed to get task status');
    }

    return response.json();
  }

  /**
   * Download processed video
   */
  async downloadVideo(taskId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/download/${taskId}`);
    
    if (!response.ok) {
      throw new Error('Failed to download video');
    }

    return response.blob();
  }

  /**
   * Create WebSocket connection for progress tracking
   */
  createProgressWebSocket(taskId: string, onMessage: (progress: ProgressMessage) => void): WebSocket {
    // Use the same host as the API but with ws protocol
    const wsUrl = `ws://localhost:8000/ws/${taskId}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      try {
        const progress: ProgressMessage = JSON.parse(event.data);
        onMessage(progress);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to parse progress message:', error);
        }
      }
    };

    ws.onerror = (error) => {
      if (import.meta.env.DEV) {
        console.error('WebSocket error:', error);
      }
    };

    ws.onopen = () => {
      if (import.meta.env.DEV) {
        console.log('WebSocket connected for task:', taskId);
      }
    };

    ws.onclose = () => {
      if (import.meta.env.DEV) {
        console.log('WebSocket disconnected for task:', taskId);
      }
    };

    return ws;
  }

  /**
   * Check API health
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      const data = await response.json();
      return data.status === 'ok';
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Health check failed:', error);
      }
      return false;
    }
  }

  /**
   * Get download URL for direct download
   */
  getDownloadUrl(taskId: string): string {
    return `${API_BASE_URL}/download/${taskId}`;
  }

  /**
   * Send payment notification
   */
  async sendPaymentNotification(request: PaymentNotificationRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payment-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to send payment notification');
    }
  }
}

export const apiService = new ApiService();
